import React, { useEffect, useRef, useState } from 'react';
import {
  ApplePaymentSelector,
  GooglePaymentSelector,
  PaymentCardTypeWrapper,
  PaymentCheckbox,
  PaymentCheckboxWrapper,
  PaymentContainer,
  PaymentGoogleIcon,
  PaymentSelector,
  PaymentSelectorIcon,
  PaymentSelectorTitle,
  PaymentSelectorWrapper,
  WalletWrapper,
} from './PaymentStyledComponents';
import { MainButton } from '../../styles/GlobalStyledComponents';
import {
  FlowEnum,
  PaymentResponse,
  PaymentMethodEnum,
  PaymentSelectorEnum,
  PaymentWindow,
  PaymentState,
  PaymentType,
  PaymentTypesEnum,
  TransactionReasonEnum,
  areEqualStatuses,
} from '../../interfaces/Payment';
import CardIcon from '../../assets/images/payment/creditCardIcon.svg';
import GooglePayIcon from '../../assets/images/payment/googlePayIcon.svg';
import ApplePayIcon from '../../assets/images/payment/applePayIcon.svg';
import ExpressIcon from '../../assets/images/payment/expressIcon.svg';
import TokenService from '../../services/Token';
import { useCustomer } from '../../contexts/CustomerContext';
import PaymentService from '../../services/Payment';
import { useCreditProposalResult } from '../../contexts/CreditProposalResultContext';
import { PaymentHandle } from '@pepper/google-apple-pay-react';
import { FeatureFlagService } from '@pepper/feature-flags-react';
import { ROUTES_PATH } from '../../constants';
import { useMerchant } from '../../contexts/MerchantContext';
import CustomerDeviceService from '../../services/CustomerDevice';
import { useCustomerClassification } from '../../contexts/CustomerClassificationContext';
import { CreditProposalResult } from '../../interfaces/CreditProposalResult';
import { CustomerClassificationEnum } from '../../interfaces/CustomerClassification';
import CreditProposalService from '../../services/CreditProposal';
import { usePaymentCard } from '../../contexts/PaymentCardContext';
import { useNavigate } from 'react-router-dom';
import { PepperTpv } from '@pepper/tpv-react';
import InsightService from '../../services/Insight';
import PostMessageService from '../../services/PostMessage';
import { DecisionStatus } from '../../interfaces/DecisionEngine';
import { findCardType } from '../../utils/existingCardHelper';
import { StoredPaymentCard } from '../../interfaces/StoredPaymentCards';
import { ReusePaymentCardStatus } from '../../interfaces/ReusePaymentCardRequest';
import { ErrorCard } from '../../components/ErrorCard';
import { environment } from '../../environments/environment';
import { generateSessionId } from '../../utils/encodingHelper';
import AnalyticsService from '../../services/Analytics';
import { DenialStep } from '../../interfaces/Analytics';
import dayjs from 'dayjs';
import AnalyticsHelper from '../../utils/analyticsHelper';
import { useProduct } from '../../contexts/ProductContext';
import FlowEndService from '../../services/FlowEndService';
interface PaymentProps {
  loadingEvent: (data: boolean) => void;
  closingEvent: (data: boolean) => void;
}

export enum environmentType {
  development = 'development',
  staging = 'staging',
  production = 'production',
}

export interface EnvType {
  type: any;
}
let loop: number | any;
let paymentData: PaymentResponse | null = null;
export const Payment: React.FC<PaymentProps> = ({
  loadingEvent,
  closingEvent,
}) => {
  const [methodSelected, setMethodSelected] =
    useState<PaymentSelectorEnum | null>(null);
  const [paymentWindow, setPaymentWindow] = useState<PaymentWindow | null>(
    null
  );
  const [showWallet, setShowWallet] = useState<boolean>(false);
  const [googleAvailable, setGoogleAvailable] = useState<boolean>();
  const [appleAvailable, setAppleAvailable] = useState<boolean>();
  const [paymentError, setPaymentError] = useState<boolean>(false);
  const [tpvPayment, setTpvPayment] = useState<PaymentResponse | null>(null);
  const [walletPayment, setWalletPayment] = useState<PaymentResponse | null>(
    null
  );
  const [tpvSubtitle, setTpvSubtitle] = useState('');
  const [existingCardSelected, setExistingCardSelected] =
    useState<StoredPaymentCard | null>(null);
  const [retries, setRetries] = useState<number>(1);

  const paymentDone = useRef<boolean>(false);

  const { customer } = useCustomer();
  const { creditProposalResult } = useCreditProposalResult();
  const { customerClassification } = useCustomerClassification();
  const { isNemuru, isPostDataMessages } = useMerchant();
  const { createPaymentCards, paymentCards, deletePaymentCards } =
    usePaymentCard();
  const { product } = useProduct();
  const [successEnd, setSuccessEnd] = useState(false);

  const navigate = useNavigate();

  const cexToken = TokenService.getToken()!;
  const currentEnv: EnvType = {
    type: import.meta.env.VITE_ENV!,
  };

  const maxAttempts = 40;
  const startTime = dayjs();

  let decisionStatus: any = { signatureReady: false, isDecisionPending: true };

  // Choose initial screen. If wallet or existing cards aren't available, redirect to TPV
  const selectInitScreen = async () => {
    if (isPostDataMessages) {
      closingEvent(false);
      PostMessageService.PaymentInitiated(
        creditProposalResult?.credit_proposal_id
      );
    }
    if (isNemuru) {
      threatMetrix();
    }
    const storedCards = await getStoredPaymentCards(creditProposalResult);
    const isWallet = await checkWallet();
    if (isWallet) {
      setShowWallet(true);
      setPaymentWindow(PaymentWindow.SELECTOR);
    } else if (!storedCards || (storedCards && storedCards.length === 0)) {
      await createPayment();
      setPaymentWindow(PaymentWindow.TPV);
    } else {
      setPaymentWindow(PaymentWindow.SELECTOR);
    }
    loadingEvent(false);
  };
  // Check conditions to display Google Pay and/or Apple Pay selector options
  const checkWallet = async () => {
    const azureAppConfig = import.meta.env.VITE_AZURE_CONFIG_STRING;
    const flagKey = import.meta.env.VITE_AZURE_CONFIG_FLAG_KEY;
    if (
      CustomerDeviceService.getCustomerDevice() &&
      !isNemuru &&
      azureAppConfig &&
      flagKey
    ) {
      const enabled = (await FeatureFlagService.isFunctionEnabled(
        azureAppConfig,
        flagKey
      )) as boolean;
      if (enabled) {
        return true;
      }
    }
    return false;
  };
  // Handle Google Pay or Apple Pay selection.
  // It sends an event to wallet library (imported as PaymentHandle) to display their own button
  const appleOrGoogleSelected = () => {
    if (methodSelected === PaymentSelectorEnum.GOOGLE) {
      return { show: true, payment: PaymentTypesEnum.GOOGLE };
    } else if (methodSelected === PaymentSelectorEnum.APPLE) {
      return { show: true, payment: PaymentTypesEnum.APPLE };
    } else {
      return { show: false, payment: PaymentTypesEnum.GOOGLE };
    }
  };
  // Check if there are existing payment cards available
  const getStoredPaymentCards = async (
    creditProposal: CreditProposalResult | undefined
  ) => {
    if (
      customerClassification?.customer_classification ===
      CustomerClassificationEnum.known ||
      customerClassification?.customer_classification ===
      CustomerClassificationEnum.knownMiniPlus
    ) {
      const storedPaymentCards =
        await CreditProposalService.getStoredPaymentCards(
          creditProposal!.credit_proposal_id
        );
      if (storedPaymentCards.payment_cards.length >= 1) {
        const cardsDisplayed = storedPaymentCards.payment_cards.slice(0, 3);
        createPaymentCards(cardsDisplayed);
        return cardsDisplayed;
      } else {
        deletePaymentCards();
        return null;
      }
    } else {
      deletePaymentCards();
      return null;
    }
  };
  // Checkbox handling of option selected
  const changeOptionSelected = (
    method: PaymentSelectorEnum,
    card?: StoredPaymentCard
  ) => {
    setMethodSelected(method);
    if (
      (method === PaymentSelectorEnum.GOOGLE ||
        method === PaymentSelectorEnum.APPLE) &&
      !walletPayment
    ) {
      createPayment(method);
    } else {
      if (card) {
        setExistingCardSelected(card);
      } else if (existingCardSelected) {
        setExistingCardSelected(null);
      }
    }
  };
  // API call to create new payment process
  const createPayment = async (method?: PaymentSelectorEnum) => {
    try {
      if (creditProposalResult?.credit_proposal_id) {
        const paymentMethod =
          method === PaymentSelectorEnum.GOOGLE ||
            method === PaymentSelectorEnum.APPLE
            ? PaymentMethodEnum.WALLET
            : PaymentMethodEnum.CREDIT_CARD;
        const data = await PaymentService.getPaymentAuthorization(
          creditProposalResult?.credit_proposal_id,
          paymentMethod
        );
        if (
          method === PaymentSelectorEnum.GOOGLE ||
          method === PaymentSelectorEnum.APPLE ||
          methodSelected === PaymentSelectorEnum.APPLE ||
          methodSelected === PaymentSelectorEnum.GOOGLE
        ) {
          setWalletPayment(data);
        } else {
          setTpvPayment(data);
          if (CustomerDeviceService.getCustomerDevice() || isNemuru) {
            setTpvSubtitle(
              'Haremos una retención de <span>' +
              data.amount.toFixed(2).toString().replace('.', ',') +
              ' €</span> por valor de la primera cuota. El cargo definitivo se realizará tras la firma del contrato'
            );
          } else {
            setTpvSubtitle(
              'Recuérdale a tu cliente que pagará la primera cuota de <span>' +
              data.amount.toFixed(2).toString().replace('.', ',') +
              ' €</span>, a la firma del contrato'
            );
          }
        }
        clearInterval(loop);
        startFetchingPaymentStatus(30000, true);
      }
    } catch (error: any) {
      if (
        error.response?.data?.result?.errors.find(
          (er: any) => er.code === 'DRF0043'
        ) &&
        creditProposalResult
      ) {
        getPADecision(creditProposalResult.credit_proposal_id, 0);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };

  useEffect(() => {
    paymentData =
      methodSelected === PaymentSelectorEnum.GOOGLE ||
        methodSelected === PaymentSelectorEnum.APPLE
        ? walletPayment
        : tpvPayment;
  }, [tpvPayment, walletPayment, methodSelected]);
  // Pooling for payment creation
  const startFetchingPaymentStatus = (
    miliseconds: number,
    isInitFeching?: boolean
  ) => {

    let currentAttempts = 0;

    loop = setInterval(async () => {
      currentAttempts++;
      if (currentAttempts >= maxAttempts || paymentDone.current) {
        clearInterval(loop);
        return;
      }

      if (paymentDone.current === false) {
        if (paymentData?.payment_id && creditProposalResult) {
          try {
            const paymentStatusObject = await PaymentService.getPaymentStatus(
              creditProposalResult?.credit_proposal_id,
              paymentData.payment_id
            );
            InsightService.logTrace('PaymentStatus', [
              { creditProposalId: creditProposalResult.credit_proposal_id },
              { paymentId: paymentData.payment_id },
              { paymentStatusObject: paymentStatusObject?.status },
            ]);
            if (
              (paymentStatusObject?.status.type === PaymentType.BLO &&
                paymentStatusObject?.status.status === PaymentState.OK) ||
              (paymentStatusObject?.status.type === PaymentType.COB &&
                paymentStatusObject?.status.status === PaymentState.OK)
            ) {
              setTpvPayment(null);
              clearInterval(loop);
              paymentDone.current = true;
              AnalyticsService.addPaymentInfo({
                value: paymentStatusObject.amount,
                currency: 'EUR',
                items: [{
                  item_id: product?.code || creditProposalResult.product,
                  item_name: product?.name || creditProposalResult.product,
                }]
              });
              let paymentTypeData;
              switch (methodSelected) {
                case PaymentSelectorEnum.CREDIT_CARD:
                  paymentTypeData = 'CARD'
                  break;
                case PaymentSelectorEnum.EXPRESS:
                  paymentTypeData = 'REUSED'
                  break;
                case PaymentSelectorEnum.GOOGLE:
                case PaymentSelectorEnum.APPLE:
                  paymentTypeData = 'WALLET';
                  break;
                default:
                  paymentTypeData = 'CARD';
                  break;
              }

              AnalyticsService.continuePayment({
                payment_type: paymentTypeData,
                has_previous_cards: (paymentCards && paymentCards.length > 0),
                amount: paymentStatusObject.amount,
                third: paymentStatusObject.third_party,
                time_elapsed: AnalyticsHelper.getTimeElapse({ initTime: startTime, finishTime: dayjs() }),
                step_number: 5,
              })
              getPADecision(creditProposalResult.credit_proposal_id, 0);
            }
            if (isInitFeching && currentAttempts >= 3) {
              clearInterval(loop);
              startFetchingPaymentStatus(15000);
            }
          } catch (error) {
            if (
              error.response?.data?.result?.errors.find(
                (er: any) => er.code === 'DRF0043'
              ) &&
              creditProposalResult
            ) {
              getPADecision(creditProposalResult.credit_proposal_id, 0);
            } else {
              navigate(ROUTES_PATH.UNKNOWN_ERROR);
            }
          }

        } else {
          clearInterval(loop);
        }
      }
    }, miliseconds);
  };
  // Payment status OK. Getting PA decision result to continue the process.
  const getPADecision = async (creditProposalId: string, counter: number) => {
    if (counter === 0 && isPostDataMessages) {
      PostMessageService.PaymentSuccessful(
        creditProposalResult?.credit_proposal_id
      );
    }
    counter++;
    try {
      const decisionResp = await CreditProposalService.getDecision(
        creditProposalId
      );
      if (decisionResp) {
        switch (decisionResp.decision) {
          case DecisionStatus.APPROVED:
          case DecisionStatus.NOT_NECESSARY:
            loadingEvent(false);
            decisionStatus = { signatureReady: true, isDecisionPending: false };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.PENDING:
            decisionStatus = { signatureReady: true, isDecisionPending: true };
            navigate(ROUTES_PATH.SIGNATURE, { state: { decisionStatus } });
            break;
          case DecisionStatus.DENIED:
            AnalyticsService.sendLoanRefused({ denial_step: DenialStep.PAC });
            navigate(ROUTES_PATH.DENIAL);
            break;
          case DecisionStatus.MISSING_DATA:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
          default:
            navigate(ROUTES_PATH.UNKNOWN_ERROR);
            break;
        }
        loadingEvent(false);
      }
    } catch (error) {
      if (`${error}`.includes('409') && counter <= 11) {
        setTimeout(() => {
          getPADecision(creditProposalId, counter);
        }, 3000);
      } else {
        loadingEvent(false);
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }
  };
  // API call to reuse payment card
  const reuseCard = async () => {
    loadingEvent(true);
    try {
      const reusePayment = await CreditProposalService.postReusePaymentCard(
        creditProposalResult?.credit_proposal_id!,
        existingCardSelected?.payment_id!,
        existingCardSelected?.payment_method_id!
      );
      switch (reusePayment?.status?.status) {
        case ReusePaymentCardStatus.OK:
          AnalyticsService.addPaymentInfo({
            value: reusePayment.amount,
            currency: 'EUR',
            items: [{
              item_id: product?.code || creditProposalResult.product,
              item_name: product?.name || creditProposalResult.product,
            }]
          });

          AnalyticsService.continuePayment({
            payment_type: 'REUSED',
            has_previous_cards: true,
            amount: reusePayment.amount,
            third: 'REUSED',
            time_elapsed: AnalyticsHelper.getTimeElapse({ initTime: startTime, finishTime: dayjs() }),
            step_number: 5,
          })
          getPADecision(creditProposalResult!.credit_proposal_id, 0);
          break;
        case ReusePaymentCardStatus.KO:
          setPaymentError(true);
          AnalyticsService.paymentError();
          loadingEvent(false);
          break;
        case ReusePaymentCardStatus.PND:
          clearInterval(loop);
          handleReusePendingStatus(reusePayment.payment_id);
          break;
        default:
          setPaymentError(true);
          loadingEvent(false);
          break;
      }
    } catch (error) {
      setPaymentError(true);
      loadingEvent(false);
    }
  };

  const handleSuccess = () => {
    clearInterval(loop);
    setSuccessEnd(true);
    loadingEvent(true);
    startFetchingPaymentStatus(3000, true);
  };

  // Pooling for reuse payment card
  const handleReusePendingStatus = async (paymentId: string) => {
    let loanCreated = false;
    let currentAtempts = 0;
    loop = setInterval(async () => {
      currentAtempts++;
      if (
        currentAtempts >= environment.pooling_max_iterations_existing_payment
      ) {
        clearInterval(loop);
        setPaymentError(true);
        AnalyticsService.paymentError();
        loadingEvent(false);
        // handleError();
      }
      let paymentStatusObject = await PaymentService.getPaymentStatus(
        creditProposalResult?.credit_proposal_id!,
        paymentId
      );
      const status = paymentStatusObject.status;
      if (
        areEqualStatuses(status, {
          type: PaymentType.REC,
          status: PaymentState.OK,
        }) &&
        !loanCreated
      ) {
        clearInterval(loop);
        AnalyticsService.addPaymentInfo({
          value: paymentStatusObject.amount,
          currency: 'EUR',
          items: [{
            item_id: product?.code || creditProposalResult.product,
            item_name: product?.name || creditProposalResult.product,
          }]
        });

        AnalyticsService.continuePayment({
          payment_type: 'REUSED',
          has_previous_cards: true,
          amount: paymentStatusObject.amount,
          third: 'REUSED',
          time_elapsed: AnalyticsHelper.getTimeElapse({ initTime: startTime, finishTime: dayjs() }),
          step_number: 5,
        })
        getPADecision(creditProposalResult!.credit_proposal_id, 0);
      }
    }, environment.pooling_interval * 1000);
  };
  // Handle main button click
  const handleContinue = async () => {
    loadingEvent(true);
    if (existingCardSelected) {
      await reuseCard();
    } else {
      await createPayment();
      loadingEvent(false);
      setPaymentWindow(PaymentWindow.TPV);
    }
  };
  // Handle payment error
  const handleTpvError = () => {
    AnalyticsService.paymentError();
    //recibimos el número de reintentos y comprobamos si sigue en rango. En ese caso haríamos el flujo habitual de crear un nuevo pago
    if (retries < 3) {
      setRetries(retries + 1);
      loadingEvent(true);
      clearInterval(loop);
      setTpvPayment(null);
      createPayment();
      setPaymentWindow(PaymentWindow.TPV);
      loadingEvent(false);
      if (isPostDataMessages) {
        PostMessageService.PaymentRetried(
          creditProposalResult?.credit_proposal_id
        );
      }
    } else {
      //Y en el caso de que agote los reintentos se manda a pantalla de error y se comunica a Nemuru que falló el pago
      if (isPostDataMessages) {
        PostMessageService.PaymentFailed(
          creditProposalResult?.credit_proposal_id
        );
      }
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }
  };

  const handleErrorRedirection = () => {
    loadingEvent(true);
    if (
      methodSelected === PaymentSelectorEnum.GOOGLE ||
      methodSelected === PaymentSelectorEnum.APPLE
    ) {
      setWalletPayment(null);
      createPayment(PaymentSelectorEnum.GOOGLE);
    } else if (methodSelected === PaymentSelectorEnum.EXPRESS && paymentCards) {
      const newCardExist = paymentCards.filter(
        (paymentCard: any) => paymentCard !== existingCardSelected
      );
      createPaymentCards(newCardExist);
    }
    setPaymentError(false);
    setPaymentWindow(PaymentWindow.SELECTOR);
    loadingEvent(false);
  };

  const threatMetrix = async () => {
    try {
      if (creditProposalResult?.credit_proposal_id) {
        const sessionId = await generateSessionId(
          creditProposalResult.order_id
        );
        if (sessionId) {
          CreditProposalService.threatMetrix(
            creditProposalResult?.credit_proposal_id,
            sessionId
          );
        }
      }
    } catch (error: any) {
      InsightService.logTrace('Error al lanzar ThreatMetrix ', [
        { error: error },
      ]);
    }
  };

  useEffect(() => {
    if (FlowEndService.getPepperOneEnded() === 'END') {
      loadingEvent(true);
      setTimeout(() => {
        navigate(ROUTES_PATH.END);
      }, 1000);
    } else {
      selectInitScreen();
    }
    return () => {
      clearInterval(loop);
    }
  }, []);

  return (
    <PaymentContainer>
      {paymentError ? (
        <ErrorCard
          paymentMethod={methodSelected}
          buttonClick={() => handleErrorRedirection()}
        />
      ) : paymentWindow === PaymentWindow.SELECTOR ? (
        <>
          <PaymentSelectorWrapper>
            {paymentCards &&
              paymentCards.length > 0 &&
              paymentCards.map((card: StoredPaymentCard) => {
                return (
                  <PaymentSelector
                    key={card.payment_method_id}
                    id="payment-input-existing-payment"
                    onClick={() =>
                      changeOptionSelected(PaymentSelectorEnum.EXPRESS, card)
                    }
                  >
                    <PaymentCheckboxWrapper>
                      <PaymentCheckbox
                        type="checkbox"
                        checked={
                          methodSelected === PaymentSelectorEnum.EXPRESS &&
                          existingCardSelected === card
                        }
                        readOnly
                      />
                      <PaymentSelectorTitle>
                        Paga en un click
                      </PaymentSelectorTitle>
                    </PaymentCheckboxWrapper>
                    <PaymentCardTypeWrapper>
                      {findCardType(card.iin_number)}
                      ···· {card.last4_digits}
                    </PaymentCardTypeWrapper>
                    <PaymentSelectorIcon src={ExpressIcon} />
                  </PaymentSelector>
                );
              })}
            <PaymentSelector
              id="payment-input-tpv"
              onClick={() =>
                changeOptionSelected(PaymentSelectorEnum.CREDIT_CARD)
              }
            >
              <PaymentCheckboxWrapper>
                <PaymentCheckbox
                  type="checkbox"
                  checked={methodSelected === PaymentSelectorEnum.CREDIT_CARD}
                  readOnly
                />
                <PaymentSelectorTitle>
                  {paymentCards && paymentCards.length > 0
                    ? 'Paga con otra tarjeta'
                    : 'Paga con tarjeta'}
                </PaymentSelectorTitle>
              </PaymentCheckboxWrapper>
              <PaymentSelectorIcon src={CardIcon} />
            </PaymentSelector>
            {showWallet && googleAvailable && customer.document_id && (
              <GooglePaymentSelector
                id="payment-input-google-pay"
                onClick={() => changeOptionSelected(PaymentSelectorEnum.GOOGLE)}
              >
                <PaymentCheckboxWrapper>
                  <PaymentCheckbox
                    type="checkbox"
                    checked={methodSelected === PaymentSelectorEnum.GOOGLE}
                    readOnly
                  />
                  <PaymentSelectorTitle>
                    Paga con Google Pay
                  </PaymentSelectorTitle>
                </PaymentCheckboxWrapper>
                <PaymentGoogleIcon src={GooglePayIcon} />
              </GooglePaymentSelector>
            )}
            {showWallet && appleAvailable && customer.document_id && (
              <ApplePaymentSelector
                id="payment-input-apple-pay"
                onClick={() => changeOptionSelected(PaymentSelectorEnum.APPLE)}
              >
                <PaymentCheckboxWrapper>
                  <PaymentCheckbox
                    type="checkbox"
                    checked={methodSelected === PaymentSelectorEnum.APPLE}
                    readOnly
                  />
                  <PaymentSelectorTitle>
                    Paga con Apple Pay
                  </PaymentSelectorTitle>
                </PaymentCheckboxWrapper>
                <PaymentSelectorIcon src={ApplePayIcon} />
              </ApplePaymentSelector>
            )}
          </PaymentSelectorWrapper>
          <MainButton
            $isHidden={
              methodSelected === PaymentSelectorEnum.GOOGLE ||
                methodSelected === PaymentSelectorEnum.APPLE
                ? 'true'
                : undefined
            }
            disabled={!methodSelected}
            onClick={handleContinue}
          >
            Continuar
          </MainButton>
          {customer?.document_id &&
            <WalletWrapper
              visible={
                methodSelected === PaymentSelectorEnum.GOOGLE ||
                methodSelected === PaymentSelectorEnum.APPLE
              }
            >
              <PaymentHandle
                publicKey={import.meta.env.VITE_ZRU_PUBLIC_KEY ?? ''}
                token={cexToken}
                isLoading={() => { }}
                isAppleAvailable={(e: any) => setAppleAvailable(e)}
                isGoogleAvailable={(e: any) => setGoogleAvailable(e)}
                handleError={() => { setPaymentError(true); AnalyticsService.paymentError(); }}
                environment={currentEnv.type}
                handleSuccess={handleSuccess}
                paymentSelected={{
                  payment_id: walletPayment?.payment_id,
                  transaction_reason: TransactionReasonEnum.POP,
                  flow: FlowEnum.ECOMMERCE,
                }}
                userDocument={customer.document_id}
                showGoogleOrAppleBtn={appleOrGoogleSelected()}
              />
            </WalletWrapper>
          }
        </>
      ) : (
        paymentWindow === PaymentWindow.TPV &&
        cexToken &&
        environment &&
        tpvPayment &&
        !successEnd && (
          <PepperTpv
            token={cexToken ?? ''}
            currentEnvironment={currentEnv}
            successEnd={handleSuccess}
            error={handleTpvError}
            paymentId={tpvPayment?.payment_id}
            subtitle={tpvSubtitle}
            subtitlePosition="top"
            generalPadding="10px 0"
            saveRedirectUrls={(e: any) => {
              /*console.log(e)*/
            }}
            buttonBackgroundColor="#ff3333"
            buttonTextColor="#ffebeb"
            stepEvent={() => { }} />
        )
      )}
    </PaymentContainer>
  );
};
